import { computed, readonly, type ComputedRef, watch, onBeforeUnmount } from "vue";
import { storeToRefs } from "pinia";
import { useLayoutStore } from "src/stores/layout-store";

/**
 * Custom composition function for layout management.
 * @returns An object containing layout config and state, and utility methods.
 */
export function useLayout() {
  const layoutStore = useLayoutStore();
  const { layoutConfig, layoutState } = storeToRefs(layoutStore);

  const tableSizeClass = computed(() => {
    if (layoutConfig.value.tableSize === "small") {
      return "sm";
    }

    if (layoutConfig.value.tableSize === "large") {
      return "lg";
    }

    return "";
  });

  const setScale = (scale: number) => {
    layoutConfig.value.scale = scale;
  };

  const setTableSize = (size: "small" | "normal" | "large") => {
    layoutConfig.value.tableSize = size;
  };

  const setPrimary = (value: string) => {
    layoutConfig.value.primary = value;
  };

  const setSurface = (value: string) => {
    layoutConfig.value.surface = value;
  };

  const setPreset = (value: string) => {
    layoutConfig.value.preset = value;
  };

  const setActiveMenuItem = (item) => {
    layoutState.value.activeMenuItem = item.value || item;
  };

  const setMenuMode = (mode) => {
    layoutConfig.value.menuMode = mode;
  };

  const systemDarkMode = window.matchMedia('(prefers-color-scheme: dark)');

  const setTheme = (theme: 'system' | 'dark' | 'light') => {
    layoutConfig.value.theme = theme;

    if (theme === 'system') {
      layoutConfig.value.darkTheme = null;
      setDarkMode(systemDarkMode.matches);
    } else {
      setDarkMode(theme === 'dark');
    }
  };

  const executeDarkModeSet = (isDark: boolean) => {
    layoutConfig.value.darkTheme = isDark;
    document.documentElement.classList.toggle('app-dark', isDark);
  };

  const setDarkMode = (isDark: boolean) => {
    if (!document.startViewTransition) {
      executeDarkModeSet(isDark);
      return;
    }
    document.startViewTransition(() => executeDarkModeSet(isDark));
  };

  const onMenuToggle = () => {
    if (layoutConfig.value.menuMode === "overlay") {
      layoutState.value.overlayMenuActive = !layoutState.value.overlayMenuActive;
    }

    if (window.innerWidth > 991) {
      layoutState.value.staticMenuDesktopInactive = !layoutState.value.staticMenuDesktopInactive;
    } else {
      layoutState.value.staticMenuMobileActive = !layoutState.value.staticMenuMobileActive;
    }
  };

  const resetMenu = () => {
    layoutState.value.overlayMenuActive = false;
    layoutState.value.staticMenuMobileActive = false;
    layoutState.value.menuHoverActive = false;
  };

  const isSidebarActive = computed(() => layoutState.value.overlayMenuActive || layoutState.value.staticMenuMobileActive);





  return {
    layoutConfig: readonly(layoutConfig),
    layoutState: readonly(layoutState),
    onMenuToggle,
    isSidebarActive,
    setActiveMenuItem,
    setTheme,
    setPrimary,
    setSurface,
    setPreset,
    resetMenu,
    setMenuMode,
    setScale,
    setTableSize,
    tableSizeClass,
    setDarkMode,
    systemDarkMode,
  };
}
